import { useEffect } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { MapPin, MessageSquare, ReceiptText, Briefcase } from "lucide-react";
import { Badge, Rating, Verified, Feature } from "../components/ui/kit";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { useListing } from "../lib/useListing";
import { setMeta, resetMeta } from "../lib/seo";
import { companies as mockCompanies } from "../data/mock";
import { SaveButton } from "../components/SaveButton";
import { MobileActionBar } from "../components/MobileActionBar";
import { useAuth } from "../lib/auth";
import { messagesHref } from "../lib/actions";

export default function CompanyDetail() {
  const [, params] = useRoute("/c/:id");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const id = params?.id || "";
  const { item: c, state } = useListing("companies", id, mockCompanies);

  useEffect(() => {
    if (c) setMeta({ title: `${c.name} — KontrataPH`, description: [c.tagline, c.city].filter(Boolean).join(" · ") || undefined });
    return () => resetMeta();
  }, [c]);

  if (state === "loading") return <DetailLoading label="Loading company…" />;
  if (state === "notfound" || !c) return <DetailNotFound title="Company not found" backHref="/marketplace?tab=Companies" backLabel="Browse Companies" />;

  const tags: string[] = Array.isArray(c.tags) ? c.tags : [];

  // CTA logic shared by the header buttons and the mobile sticky bar
  const isSelf = !!user && c.ownerUserId === user.id;
  const msgHref = messagesHref(c.ownerUserId, { type: "company", id, name: c.name });
  const quoteHref = messagesHref(c.ownerUserId, { type: "company", id, name: c.name, intent: "quote" });
  const disabledReason = isSelf ? "This is your company" : "Not available on this sample company";
  const go = (target: string | null) => () => { if (!user) return navigate("/login"); if (target) navigate(target); };
  const ctaDisabled = !!user && (isSelf || !msgHref);

  return (
    <div className="container-x py-6 pb-24 lg:pb-6">
      <div className="text-sm text-mut mb-4"><Link href="/marketplace?tab=Companies" className="hover:text-navy">Companies</Link> / {c.name}</div>
      <div className="card p-6 flex flex-col sm:flex-row gap-5 items-start">
        <div className="w-20 h-20 rounded-xl bg-navy text-white text-2xl font-bold grid place-items-center shrink-0">{c.initials}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap"><h1 className="text-2xl font-extrabold text-navy">{c.name}</h1>{c.verified && <Verified label="Verified Company" />}</div>
          <p className="text-mut">{c.tagline}</p>
          <div className="flex flex-wrap gap-2 mt-2">{tags.map((t) => <Badge key={t}>{t}</Badge>)}</div>
          <p className="text-sm text-mut flex items-center gap-1 mt-2"><MapPin className="w-4 h-4" />{c.city}{c.since ? ` · Member since ${c.since}` : ""}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            {user
              ? <SaveButton kind="company" refId={id} label="Follow" />
              : <button onClick={() => navigate("/login")} className="btn-outline">Follow</button>}
            <button onClick={go(msgHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"><MessageSquare className="w-4 h-4" />Contact</button>
            <button onClick={go(quoteHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined}
              className="btn-red disabled:opacity-50 disabled:cursor-not-allowed"><ReceiptText className="w-4 h-4" />Request Quote</button>
          </div>
        </div>
        <div className="text-center">
          <Rating value={c.rating} count={c.reviews} />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5"><h3 className="font-bold text-navy mb-2">About {c.name}</h3><p className="text-sm text-mut">{c.tagline || "A full-service company delivering quality solutions on time and within budget."}</p></div>
          <div className="card p-5"><h3 className="font-bold text-navy mb-3">Focus Areas</h3>
            {tags.length ? (
              <div className="grid sm:grid-cols-3 gap-3">
                {tags.map((s) => (
                  <div key={s} className="border border-line rounded-lg p-3"><div className="font-semibold text-navy text-sm">{s}</div><p className="text-xs text-mut mt-1">End-to-end services for residential and commercial projects.</p></div>
                ))}
              </div>
            ) : <p className="text-sm text-mut">No focus areas listed yet.</p>}
          </div>
        </div>
        <div className="space-y-4">
          <div className="card p-5"><h3 className="font-bold text-navy mb-3 flex items-center gap-2"><Briefcase className="w-4 h-4" />Get in touch</h3>
            <p className="text-sm text-mut">Follow this company to see new services, jobs, and projects, or request a quotation directly.</p>
          </div>
          <div className="card p-5"><h3 className="font-bold text-navy mb-3">Company Statistics</h3>
            <div className="grid grid-cols-2 gap-3 text-center">
              {[[String(c.reviews ?? 0), "Reviews"], [c.rating ? c.rating.toFixed(1) : "—", "Rating"], [c.since || "—", "Since"], [c.verified ? "Yes" : "—", "Verified"]].map(([b, l]) => (
                <div key={l} className="border border-line rounded-lg p-3"><div className="text-xl font-extrabold text-navy">{b}</div><div className="text-xs text-mut">{l}</div></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <MobileActionBar>
        <button onClick={go(msgHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined}
          className="btn-outline flex-1 disabled:opacity-50 disabled:cursor-not-allowed"><MessageSquare className="w-4 h-4" />Contact</button>
        <button onClick={go(quoteHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined}
          className="btn-red flex-1 disabled:opacity-50 disabled:cursor-not-allowed"><ReceiptText className="w-4 h-4" />Request Quote</button>
      </MobileActionBar>
    </div>
  );
}
